import ScenarioUpdateNotice from './ScenarioUpdateNotice';
import type { ScenarioNotice } from '../../../helpers/scenarioReview';
import {
  faBullseye,
  faCheckDouble,
  faChevronDown,
  faChevronUp,
  faPenNib,
  faReceipt,
  faTriangleExclamation,
  faUserSecret,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';

import { MissionConfigurationDetailType, MissionPromotionDisplayDto } from '../../../api/api.g';
import useStyles from '../../../hooks/styles-hook';
import useTranslation from '../../../hooks/translation-hook';
import CustomMarkdown from '../CustomMarkDown';
import Label, { LabelType } from '../Label';
import MoreInfoButton from '../MoreInfoButton';
import { MissionPromotionSection } from './MissionPromotion';

type MissionDetailAccordionSection = 'admissibility' | 'conditions';

export interface MissionDetailSectionValue {
  type?: MissionConfigurationDetailType | number;
  text?: string;
}

interface MissionDetailSectionsProps {
  details?: readonly MissionDetailSectionValue[];
  promotionDisplay?: MissionPromotionDisplayDto;
  scenarioUpdateNotice?: ScenarioNotice;
  resetKey?: string;
  onOpenScenario?: () => void;
}

const MissionDetailSections = ({ details, promotionDisplay, scenarioUpdateNotice, resetKey, onOpenScenario }: MissionDetailSectionsProps) => {
  const t = useTranslation();
  const styles = useStyles((section) => section.myMissions);
  const [expandedSection, setExpandedSection] = useState<MissionDetailAccordionSection>();

  useEffect(() => {
    setExpandedSection(undefined);
  }, [resetKey]);

  const detailTextByType = useMemo(() => {
    const result: Partial<Record<MissionConfigurationDetailType, string>> = {};

    for (const detail of details ?? []) {
      if (detail.type !== undefined) {
        result[detail.type as MissionConfigurationDetailType] = detail.text ?? '';
      }
    }

    return result;
  }, [details]);

  const renderAccordion = (section: MissionDetailAccordionSection, title: string, icon: any, content: string) => {
    if (!content) return null;

    const isExpanded = expandedSection === section;

    return (
      <View style={styles.newVersion.details.content.details.infoBlocks.accordion.base}>
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded: isExpanded }}
          style={styles.newVersion.details.content.details.infoBlocks.accordion.header}
          onPress={() => setExpandedSection((current) => (current === section ? undefined : section))}
        >
          <FontAwesomeIcon icon={icon} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
          <Label variant={LabelType.titleDark} style={styles.newVersion.details.content.details.infoBlocks.accordion.title} numberOfLines={3}>
            {title}
          </Label>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            size={16}
            style={styles.newVersion.details.content.details.infoBlocks.accordion.chevron}
          />
        </Pressable>
        {isExpanded && (
          <View style={styles.newVersion.details.content.details.infoBlocks.accordion.content}>
            <CustomMarkdown>{content}</CustomMarkdown>
          </View>
        )}
      </View>
    );
  };

  const renderDetailBlock = (
    content: string,
    title: string,
    icon: any,
    helpText?: string,
    helpUrl?: string,
    onReadMore?: () => void,
  ) => {
    if (!content) return null;

    return (
      <View style={styles.newVersion.details.content.details.infoBlocksBase}>
        <View style={styles.newVersion.details.content.details.infoBlocks.title}>
          <FontAwesomeIcon icon={icon} style={styles.newVersion.details.content.details.infoBlocks.titleIcon} />
          <Label variant={LabelType.titleDark}>{title}</Label>
          {!!helpText && (
            <MoreInfoButton style={{ marginLeft: 8 }} title={''} openURL={helpUrl}>
              {helpText}
            </MoreInfoButton>
          )}
        </View>
        <View style={styles.newVersion.details.content.details.infoBlocks.content}>
          <CustomMarkdown>{content}</CustomMarkdown>
          {!!onReadMore && (
            <Pressable style={styles.newVersion.details.content.details.infoBlocks.link} onPress={onReadMore}>
              <Label variant={LabelType.caption}>{t('Read-more')}</Label>
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  const objectiveText = detailTextByType[MissionConfigurationDetailType.Objective] ?? '';
  const noteText = detailTextByType[MissionConfigurationDetailType.ToNote] ?? '';
  const niceToKnowText = detailTextByType[MissionConfigurationDetailType.NiceToKnow] ?? '';
  const scenarioText = detailTextByType[MissionConfigurationDetailType.Scenario] ?? '';
  const eligibilityText = detailTextByType[MissionConfigurationDetailType.Eligibility] ?? '';
  const termsAndConditionsText = detailTextByType[MissionConfigurationDetailType.TermsAndConditions] ?? '';

  return (
    <>
      <ScenarioUpdateNotice notice={scenarioUpdateNotice} onRead={onOpenScenario} />
      {renderDetailBlock(objectiveText, t('Your-objective'), faBullseye, t('help.your-objective'))}
      <MissionPromotionSection display={promotionDisplay} appearance={styles.newVersion.details.content.details} />
      {renderDetailBlock(noteText, t('Please-note'), faPenNib, t('help.please-note'))}
      {renderDetailBlock(niceToKnowText, t('Pay-attention'), faTriangleExclamation, t('help.nice-to-know'))}
      {renderDetailBlock(
        scenarioText,
        t('The-scenario'),
        faUserSecret,
        t('help.the-scenario'),
        'the-scenario-lanla-website-support-link',
        onOpenScenario,
      )}
      {renderAccordion('admissibility', t('Admissibility'), faCheckDouble, eligibilityText)}
      {renderAccordion('conditions', t('Terms-and-Conditions'), faReceipt, termsAndConditionsText)}
    </>
  );
};

export default MissionDetailSections;
